import { Type_RefFuncKrpano } from '@components/Krpano/types';
import { TIME_DELAY_PAUSE_AUTOPLAYSCENE } from '@containers/Home/constants';
import { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectConfigAutoRotate,
  selectConfigAutoChangeScene,
} from '../selectors';
import { useKrpano } from './useKrpano';
import { useTourStats } from '../global/TourStats';

interface Props {
  krpanoRef: Type_RefFuncKrpano | null;
}

export const useEventKrpano = ({ krpanoRef }: Props) => {
  const { onUp, onDown, onLeft, onRight, onStopHorizontal, onStopVertical } =
    useKrpano({ krpanoRef });

  const [, setTourStats] = useTourStats('stats');
  const configAutoRotate = useSelector(selectConfigAutoRotate);
  const configAutoChangeScene = useSelector(selectConfigAutoChangeScene);
  const [isTemporaryPauseAutoChangeScene, setIsTemporaryPauseAutoChangeScene] =
    useState<boolean>(false);
  const [timePauseChangeScene, setTimePauseChangeScene] = useState<
    number | undefined
  >(undefined);

  // const intervalTimeChangeScene = useRef<any>(null!);

  // NOTE: auto change scene
  const enableAutoChangeScene = useMemo(
    () => configAutoChangeScene.enabled,
    [configAutoChangeScene.enabled],
  );
  const isAutoChangeScene = useMemo(
    () => configAutoChangeScene.on,
    [configAutoChangeScene.on],
  );
  const timeChangeScene = useMemo(
    () => configAutoChangeScene.waitTime,
    [configAutoChangeScene],
  );
  const enabledAutoRotate = useMemo(
    () => configAutoRotate.enabled,
    [configAutoRotate],
  );
  const isTurnOnAutoRotate = useMemo(
    () => configAutoRotate.on,
    [configAutoRotate],
  );

  // SECTION: auto change scene

  const onStopAutoChangeScene = useCallback(() => {
    // if (intervalTimeChangeScene.current)
    //   clearInterval(intervalTimeChangeScene.current);
    setIsTemporaryPauseAutoChangeScene(true);
    setTimePauseChangeScene(
      +(timeChangeScene || 3) + +TIME_DELAY_PAUSE_AUTOPLAYSCENE,
    );
  }, [timeChangeScene]);

  /**
   * NOTE: add event remove autochangescene
   */
  const onEventAutoChangeScene = useCallback(() => {
    krpanoRef
      ?.getContainer()
      .addEventListener('mousedown', onStopAutoChangeScene);
    krpanoRef
      ?.getContainer()
      ?.addEventListener('touchstart', onStopAutoChangeScene);
  }, [krpanoRef, onStopAutoChangeScene]);

  const offEventAutoChangeScene = useCallback(() => {
    krpanoRef
      ?.getContainer()
      ?.removeEventListener('mousedown', onStopAutoChangeScene);
    krpanoRef
      ?.getContainer()
      ?.removeEventListener('touchstart', onStopAutoChangeScene);
  }, [krpanoRef, onStopAutoChangeScene]);

  // NOTE: event move scene by keycode
  const onKeyUp = useCallback(
    e => {
      switch (e.keyCode) {
        case 37:
          onStopHorizontal();
          break;
        case 38:
          onStopVertical();
          break;
        case 39:
          onStopHorizontal();
          break;
        case 40:
          onStopVertical();
          break;
        default:
          break;
      }
    },
    [onStopHorizontal, onStopVertical],
  );

  const onKeyDown = useCallback(
    e => {
      switch (e.keyCode) {
        case 37:
          onLeft();
          break;
        case 38:
          onUp();
          break;
        case 39:
          onRight();
          break;
        case 40:
          onDown();
          break;
        default:
          break;
      }
    },
    [onDown, onLeft, onRight, onUp],
  );

  const onEventMoveByKeyCode = useCallback(() => {
    krpanoRef?.getContainer()?.addEventListener('keyup', onKeyUp);
    krpanoRef?.getContainer()?.addEventListener('keydown', onKeyDown);
  }, [krpanoRef, onKeyDown, onKeyUp]);

  const offEventMoveByKeyCode = useCallback(() => {
    krpanoRef?.getContainer()?.removeEventListener('keyup', onKeyUp);
    krpanoRef?.getContainer()?.removeEventListener('keydown', onKeyDown);
  }, [krpanoRef, onKeyDown, onKeyUp]);

  const onEventCheckGyroscopeAvailable = useCallback(() => {
    const isAvailable = krpanoRef?.get(
      'plugin[gyroscope].isavailable',
    ) as unknown;
    if (typeof isAvailable === 'boolean')
      setTourStats(prev => ({ ...prev, isGyroscopeAvailable: isAvailable }));
  }, [krpanoRef, setTourStats]);

  return useMemo(
    () => ({
      isAutoChangeScene,
      timeChangeScene,
      enableAutoChangeScene,
      isTurnOnAutoRotate,
      enabledAutoRotate,
      //
      timePauseChangeScene,
      setTimePauseChangeScene,
      isTemporaryPauseAutoChangeScene,
      setIsTemporaryPauseAutoChangeScene,
      //
      onEventMoveByKeyCode,
      offEventMoveByKeyCode,
      onEventAutoChangeScene,
      offEventAutoChangeScene,
      onEventCheckGyroscopeAvailable,
    }),
    [
      isAutoChangeScene,
      timeChangeScene,
      enableAutoChangeScene,
      isTurnOnAutoRotate,
      enabledAutoRotate,
      timePauseChangeScene,
      isTemporaryPauseAutoChangeScene,
      onEventMoveByKeyCode,
      offEventMoveByKeyCode,
      onEventAutoChangeScene,
      offEventAutoChangeScene,
      onEventCheckGyroscopeAvailable,
    ],
  );
};
